import type { OccupancyBookingSpan } from '@sea-snag/shared';

import { weekStartForColumn } from './heatmap-columns';
import { spansInWeek } from './occupancy-spans';

/**
 * Which booking occupies each cell of one boat's row, and where each booking
 * begins and ends.
 *
 * A booking covering several weeks is one booking, and the grid should say so:
 * drawn as a single continuous bar, with its price printed once at the start.
 * Rendering each week independently made a fortnight look like two charters, and
 * repeating the price made a €10,038 fortnight read as €20,076 when scanning the
 * row.
 *
 * Adjacency is judged on the booking's identity rather than on colour, so two
 * back-to-back charters stay separate bars — the changeover between them is
 * exactly what an operator is looking for.
 */

export interface CellSpanInfo {
  /** Identifies the booking occupying this cell. */
  spanKey: string;
  kind: OccupancyBookingSpan['kind'];
  isSpanStart: boolean;
  isSpanEnd: boolean;
  /** The column the booking starts at — where its single cell is rendered. */
  startColumn: number;
  /** How many columns the booking covers, for the cell's grid-column span. */
  length: number;
  /** Set only on the first cell of a span, and only when it has a price. */
  amount: number | null;
  currency: string | null;
}

const keyOf = (s: OccupancyBookingSpan) => `${s.kind}|${s.startDate}|${s.endDate}`;

/**
 * Index of the first column in this booking's unbroken run.
 *
 * A booking interrupted by a column the view does not render is two runs, and
 * each must be its own cell — spanning across a gap would draw over weeks that
 * are not there.
 */
function findRunStart(
  columns: number[],
  perColumn: Map<number, OccupancyBookingSpan>,
  i: number
): number {
  const key = keyOf(perColumn.get(columns[i]!)!);
  let j = i;
  while (j > 0) {
    const prev = perColumn.get(columns[j - 1]!);
    if (!prev || keyOf(prev) !== key) break;
    j -= 1;
  }
  return columns[j]!;
}

export function resolveRowSpans(
  columns: number[],
  year: number,
  spans: OccupancyBookingSpan[],
  boatPid: string
): Map<number, CellSpanInfo> {
  // The winning span per column, by the same precedence the cell colour uses,
  // so the bar and the colour can never disagree.
  const perColumn = new Map<number, OccupancyBookingSpan>();
  for (const col of columns) {
    const weekStart = weekStartForColumn(year, col);
    const weekEnd = new Date(Date.parse(`${weekStart}T00:00:00Z`) + 6 * 86_400_000)
      .toISOString()
      .slice(0, 10);
    const hit = spansInWeek(spans, weekStart, weekEnd, boatPid)[0];
    if (hit) perColumn.set(col, hit.span);
  }

  // Each booking is rendered as one cell spanning its columns, so it needs the
  // column it starts at and how many it covers. Two adjacent cells could be made
  // to touch, but they still read as two bookings; one wide cell cannot.
  const runLength = new Map<string, number>();
  const runStart = new Map<string, number>();
  columns.forEach((col, i) => {
    const span = perColumn.get(col);
    if (!span) return;
    const key = `${keyOf(span)}@${findRunStart(columns, perColumn, i)}`;
    runLength.set(key, (runLength.get(key) ?? 0) + 1);
    if (!runStart.has(key)) runStart.set(key, col);
  });

  const out = new Map<number, CellSpanInfo>();
  columns.forEach((col, i) => {
    const span = perColumn.get(col);
    if (!span) return;
    const key = keyOf(span);
    const runKey = `${key}@${findRunStart(columns, perColumn, i)}`;
    // Neighbours are judged among the *rendered* columns: in the season-only
    // view a booking may continue past the last drawn column, and its bar must
    // close there rather than imply a continuation nobody can see.
    const prev = i > 0 ? perColumn.get(columns[i - 1]!) : undefined;
    const next = i < columns.length - 1 ? perColumn.get(columns[i + 1]!) : undefined;
    const isSpanStart = !prev || keyOf(prev) !== key;
    const isSpanEnd = !next || keyOf(next) !== key;
    out.set(col, {
      spanKey: key,
      kind: span.kind,
      isSpanStart,
      isSpanEnd,
      startColumn: runStart.get(runKey) ?? col,
      length: runLength.get(runKey) ?? 1,
      amount: isSpanStart && span.amount != null ? span.amount : null,
      currency: isSpanStart && span.amount != null ? (span.currency ?? null) : null,
    });
  });
  return out;
}
